import {
  FloatingArrow,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useTransitionStatus,
  useTransitionStyles,
} from '@floating-ui/react';
import CloseIcon from 'assets/close.svg';
import { Button } from 'components/Button';
import { useTranslation } from 'hooks/useTranslation';
import { useUILanguage } from 'hooks/useUILanguage';
import React, {
  HTMLAttributes,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';
import styles from './Onboarding.module.scss';
import { OnboardingStep, useOnboardingState } from './state';

type ContextValue = {
  step: OnboardingStep;
  setReferenceElement: (element: HTMLDivElement | null) => void;
};

const OnboardingContext = createContext<ContextValue>({
  step: OnboardingStep.Step1,
  setReferenceElement: () => {
    throw new Error('setReferenceElement was called outside of the provider');
  },
});

type RootProps = {
  children: React.ReactNode;
  id: string;
  isHidden: boolean;
  placement: 'center' | 'start';
};

export const Root: React.FC<RootProps> = ({
  children,
  id,
  isHidden,
  placement,
}) => {
  const { step, setCompleted, goToPreviousStep, goToNextStep, isCompleted } =
    useOnboardingState();

  const arrowRef = useRef(null);
  const { context, floatingStyles, refs } = useFloating({
    open: !isCompleted && !isHidden,
    onOpenChange: (open) => !open && setCompleted(),
    placement: placement === 'center' ? 'bottom' : 'bottom-start',
    middleware: [
      offset(20),
      flip(),
      shift({ padding: 8 }),
      (placement === 'center' || step !== OnboardingStep.Step3) &&
        arrow({ element: arrowRef, padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const { status } = useTransitionStatus(context);
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context);

  const contextValue = useMemo<ContextValue>(
    () => ({
      step,
      setReferenceElement: refs.setReference,
    }),
    [refs, step]
  );

  const language = useUILanguage();
  const t = useTranslation();

  return (
    <>
      <OnboardingContext.Provider value={contextValue}>
        {children}
      </OnboardingContext.Provider>

      <div
        ref={refs.setFloating}
        className={styles.root}
        style={floatingStyles}
        data-status={status}
      >
        {isMounted && (
          <div
            role="note"
            aria-label={t('navigationOnboardingAccessibilityLabel')}
            aria-live="polite"
            className={styles.content}
            id={id}
            style={transitionStyles}
          >
            <FloatingArrow
              context={context}
              ref={arrowRef}
              width={18}
              height={8}
              className={styles.arrow}
            />
            <div className={styles.closeSection}>
              <Button
                text={t('close')}
                onClick={setCompleted}
                iconLeft={CloseIcon}
                iconOnly
                variant="text"
                theme="light"
                padding="none"
                size="md"
              />
            </div>
            <div className={styles.textSection}>
              <h3 className={styles.title}>
                {step === OnboardingStep.Step1 &&
                  t('navigationOnboardingStep1Title')}
                {step === OnboardingStep.Step2 &&
                  t('navigationOnboardingStep2Title')}
                {step === OnboardingStep.Step3 &&
                  t('navigationOnboardingStep3Title')}
                {step === OnboardingStep.Step4 &&
                  t('navigationOnboardingStep4Title')}
              </h3>
              <p className={styles.description}>
                {step === OnboardingStep.Step1 &&
                  t('navigationOnboardingStep1Description')}
                {step === OnboardingStep.Step2 &&
                  t('navigationOnboardingStep2Description')}
                {step === OnboardingStep.Step3 &&
                  t('navigationOnboardingStep3Description')}
                {step === OnboardingStep.Step4 &&
                  t('navigationOnboardingStep4Description')}
              </p>

              <div className={styles.buttons}>
                {step !== OnboardingStep.Step1 && (
                  <Button
                    text={t('navigationOnboardingPrevious')}
                    onClick={goToPreviousStep}
                    size="xs"
                    variant="text"
                    theme="light"
                  />
                )}
                {step !== OnboardingStep.Step4 && (
                  <Button
                    text={t('navigationOnboardingNext')}
                    onClick={goToNextStep}
                    size="xs"
                    variant="primary"
                    theme="light"
                  />
                )}
                {step === OnboardingStep.Step4 && (
                  <Button
                    text={t('navigationOnboardingReadMore')}
                    pointer={{
                      type: 'external-html',
                      uri:
                        language === 'sv'
                          ? 'https://svenska.yle.fi/s/10055118'
                          : 'https://yle.fi/aihe/s/10006609',
                    }}
                    size="xs"
                    variant="primary"
                    theme="light"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

type StepProps = HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  step: OnboardingStep;
};

export const Step: React.FC<StepProps> = ({
  children,
  step: thisStep,
  ...restProps
}) => {
  const { step: currentStep, setReferenceElement } =
    useContext(OnboardingContext);

  return (
    <div
      ref={currentStep === thisStep ? setReferenceElement : undefined}
      {...restProps}
    >
      {children}
    </div>
  );
};
